/**
 * Authentication Service
 *
 * Provides a unified interface for authentication that supports both:
 * - Amplify/Cognito authentication (when configured)
 * - Legacy JWT authentication (fallback)
 */

import { signIn, signOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';

// Check if Cognito is configured
const isCognitoConfigured = () => {
  return !!(
    import.meta.env.VITE_COGNITO_USER_POOL_ID &&
    import.meta.env.VITE_COGNITO_CLIENT_ID
  );
};

/**
 * Sign in with email and password
 */
export const login = async (email, password, isRetry = false) => {
  if (isCognitoConfigured()) {
    // Use Cognito authentication
    try {
      // Clear any existing session before signing in (prevents "already signed in" errors)
      if (!isRetry) {
        try {
          await signOut();
          console.log('Cleared existing session before login');
        } catch (signOutError) {
          // Ignore sign out errors - user might not be signed in
          console.log('No existing session to clear');
        }
      }

      const signInResult = await signIn({
        username: email,
        password,
      });

      console.log('Cognito signIn result:', signInResult);

      // Handle different sign-in states
      if (signInResult.isSignedIn) {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();

        // Store token for API calls
        if (token) {
          localStorage.setItem('auth_token', token);
        }

        const user = await getCurrentUser();
        return {
          success: true,
          user: {
            username: user.username,
            email: user.signInDetails?.loginId || email,
          },
        };
      }

      // Handle challenge flows
      const nextStep = signInResult.nextStep;
      if (nextStep) {
        console.log('Cognito nextStep:', nextStep);
        switch (nextStep.signInStep) {
          case 'CONFIRM_SIGN_UP':
            return { success: false, error: 'Please confirm your email before signing in.' };
          case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
            return { success: false, error: 'Password change required. Please contact administrator.', challengeName: 'NEW_PASSWORD_REQUIRED' };
          case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
          case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
            return { success: false, error: 'MFA code required.', challengeName: nextStep.signInStep };
          case 'DONE':
            // Should have been handled by isSignedIn, but just in case
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();
            if (token) {
              localStorage.setItem('auth_token', token);
            }
            const user = await getCurrentUser();
            return {
              success: true,
              user: {
                username: user.username,
                email: user.signInDetails?.loginId || email,
              },
            };
          default:
            return { success: false, error: `Unexpected authentication step: ${nextStep.signInStep}` };
        }
      }

      return { success: false, error: 'Sign in failed' };
    } catch (error) {
      console.error('Cognito sign in error:', error);

      // Handle "already signed in" error by signing out and retrying (only once)
      if (!isRetry && (
          error.name === 'UserAlreadyAuthenticatedException' ||
          error.message?.includes('already a signed in user') ||
          error.message?.includes('There is already a signed in user'))) {
        console.log('Detected existing session, signing out and retrying...');
        try {
          await signOut();
          // Retry sign in after signing out
          return login(email, password, true);
        } catch (signOutError) {
          console.error('Error signing out before retry:', signOutError);
        }
      }

      return {
        success: false,
        error: error.message || 'Authentication failed',
      };
    }
  } else {
    // Use legacy JWT authentication
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || '/api'}/auth/login`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ username: email, password }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('auth_token', data.token);
        return {
          success: true,
          user: data.user,
        };
      } else {
        // Try to parse error response as JSON, but handle non-JSON responses
        let errorMessage = 'Authentication failed';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const error = await response.json();
            errorMessage = error.detail || errorMessage;
          } else {
            // Non-JSON response (likely HTML error page or CORS issue)
            console.error('API returned non-JSON response:', response.status, response.statusText);
            if (response.status === 404) {
              errorMessage = 'API endpoint not found. Check VITE_API_URL configuration.';
            } else if (response.status === 0) {
              errorMessage = 'Network error or CORS issue. Check API configuration.';
            }
          }
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
        }
        return {
          success: false,
          error: errorMessage,
        };
      }
    } catch (error) {
      console.error('JWT sign in error:', error);
      return {
        success: false,
        error: 'Network error',
      };
    }
  }
};

/**
 * Sign out the current user
 */
export const logout = async () => {
  if (isCognitoConfigured()) {
    try {
      await signOut();
    } catch (error) {
      console.error('Cognito sign out error:', error);
    }
  }

  // Always clear local storage
  localStorage.removeItem('auth_token');
};

/**
 * Get the current authentication token
 */
export const getAuthToken = async () => {
  if (isCognitoConfigured()) {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (token) {
        // Update stored token
        localStorage.setItem('auth_token', token);
        return token;
      }
    } catch (error) {
      console.error('Error getting Cognito token:', error);
    }
  }

  // Fallback to stored token
  return localStorage.getItem('auth_token');
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = async () => {
  if (isCognitoConfigured()) {
    try {
      await getCurrentUser();
      return true;
    } catch {
      return false;
    }
  } else {
    return !!localStorage.getItem('auth_token');
  }
};

/**
 * Get current user information
 */
export const getCurrentUserInfo = async () => {
  if (isCognitoConfigured()) {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();

      return {
        username: user.username,
        email: user.signInDetails?.loginId,
        isAdmin: true, // TODO: Check Cognito groups
      };
    } catch {
      return null;
    }
  } else {
    const token = localStorage.getItem('auth_token');
    if (!token) return null;

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || '/api'}/auth/verify`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        return data.user;
      }
    } catch (error) {
      console.error('Error verifying token:', error);
    }

    return null;
  }
};

/**
 * Get authentication provider type
 */
export const getAuthProvider = () => {
  return isCognitoConfigured() ? 'cognito' : 'jwt';
};

export default {
  login,
  logout,
  getAuthToken,
  isAuthenticated,
  getCurrentUserInfo,
  getAuthProvider,
};
